import type { PointerEvent as ReactPointerEvent, RefObject } from 'react'
import type { Bounds, DragKind } from './types'

import { useCallback, useEffect, useRef } from 'react'

const MIN_W = 240
const MIN_H = 150

type DragState = {
    kind: DragKind
    uid: string
    origin: { x: number; y: number }
    start: { clientX: number; clientY: number }
    width: number
    height: number
    pointerId: number
    last: { x: number; y: number }
    lastSize: { w: number; h: number }
}

type BeginArgs = {
    kind: DragKind
    uid: string
    origin: { x: number; y: number }
    width: number
    height: number
}

type Commits = {
    onMoveWindow: (uid: string, x: number, y: number) => void
    onMoveIcon: (uid: string, x: number, y: number) => void
    onResizeWindow: (uid: string, w: number, h: number) => void
}

type UseDragGhost = {
    ghostRef: RefObject<HTMLDivElement | null>
    beginDrag: (event: ReactPointerEvent, args: BeginArgs) => void
    finishDrag: () => void
}

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), Math.max(min, max))

/**
 * One ghost, shared by window drags, icon drags and window resizes. During a
 * move we mutate the ghost element's style directly (no React re-render) and
 * only commit the last geometry on release.
 */
export const useDragGhost = (boundsRef: RefObject<Bounds>, commits: Commits): UseDragGhost => {
    const ghostRef = useRef<HTMLDivElement | null>(null)
    const dragRef = useRef<DragState | null>(null)
    const commitsRef = useRef(commits)

    commitsRef.current = commits

    const applyGhost = useCallback((x: number, y: number, w: number, h: number) => {
        const ghost = ghostRef.current

        if (!ghost) return

        ghost.style.width = `${w}px`
        ghost.style.height = `${h}px`
        ghost.style.transform = `translate3d(${x}px, ${y}px, 0)`
    }, [])

    const moveRef = useRef<(event: PointerEvent) => void>(() => undefined)
    const endRef = useRef<() => void>(() => undefined)

    // Stable listener identities so removal always matches registration.
    const onWindowMove = useCallback((event: PointerEvent) => moveRef.current(event), [])
    const onWindowEnd = useCallback(() => endRef.current(), [])

    const finishDrag = useCallback(() => {
        const drag = dragRef.current

        if (!drag) return

        dragRef.current = null

        window.removeEventListener('pointermove', onWindowMove)
        window.removeEventListener('pointerup', onWindowEnd)
        window.removeEventListener('pointercancel', onWindowEnd)

        const ghost = ghostRef.current

        if (ghost) ghost.style.display = 'none'

        if (drag.kind === 'resize') {
            commitsRef.current.onResizeWindow(drag.uid, drag.lastSize.w, drag.lastSize.h)
        } else if (drag.kind === 'icon') {
            commitsRef.current.onMoveIcon(drag.uid, drag.last.x, drag.last.y)
        } else {
            commitsRef.current.onMoveWindow(drag.uid, drag.last.x, drag.last.y)
        }
    }, [onWindowEnd, onWindowMove])

    const handleMove = useCallback((event: PointerEvent) => {
        const drag = dragRef.current

        if (!drag || event.pointerId !== drag.pointerId) return

        const bounds = boundsRef.current
        const dx = event.clientX - drag.start.clientX
        const dy = event.clientY - drag.start.clientY

        if (drag.kind === 'resize') {
            const nw = clamp(drag.width + dx, MIN_W, Math.max(MIN_W, bounds.w - drag.origin.x))
            const nh = clamp(drag.height + dy, MIN_H, Math.max(MIN_H, bounds.h - drag.origin.y))

            drag.lastSize = { w: Math.round(nw), h: Math.round(nh) }
            applyGhost(drag.origin.x, drag.origin.y, drag.lastSize.w, drag.lastSize.h)

            return
        }

        const maxX = drag.kind === 'icon'
            ? bounds.w - drag.width
            : bounds.w - Math.min(drag.width, 80)
        const maxY = drag.kind === 'icon'
            ? bounds.h - drag.height
            : bounds.h - 22

        const nx = clamp(drag.origin.x + dx, 0, maxX)
        const ny = clamp(drag.origin.y + dy, 0, maxY)

        drag.last = { x: Math.round(nx), y: Math.round(ny) }
        applyGhost(drag.last.x, drag.last.y, drag.width, drag.height)
    }, [applyGhost, boundsRef])

    moveRef.current = handleMove
    endRef.current = () => finishDrag()

    const beginDrag = useCallback((event: ReactPointerEvent, args: BeginArgs) => {
        finishDrag()

        dragRef.current = {
            kind: args.kind,
            uid: args.uid,
            origin: { ...args.origin },
            start: { clientX: event.clientX, clientY: event.clientY },
            width: args.width,
            height: args.height,
            pointerId: event.pointerId,
            last: { ...args.origin },
            lastSize: { w: args.width, h: args.height }
        }

        const target = event.currentTarget as HTMLElement

        if (target.setPointerCapture) {
            try {
                target.setPointerCapture(event.pointerId)
            } catch {
                // capture is best-effort: the window listeners are the source of truth
            }
        }

        const ghost = ghostRef.current

        if (ghost) ghost.style.display = 'block'

        applyGhost(args.origin.x, args.origin.y, args.width, args.height)

        window.addEventListener('pointermove', onWindowMove)
        window.addEventListener('pointerup', onWindowEnd)
        window.addEventListener('pointercancel', onWindowEnd)
    }, [applyGhost, finishDrag, onWindowEnd, onWindowMove])

    useEffect(() => finishDrag, [finishDrag])

    return { ghostRef, beginDrag, finishDrag }
}
